import type { ComponentProps, PropsWithChildren } from 'react'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useInView } from 'react-intersection-observer'
import { replace } from 'lib/routing/appRouting'
import { useLocation } from 'react-router-dom'

import { setSearchParamValue } from 'lib/url/searchUrlUtils'
import { getCruiseSearchStateFromParams, getCruiseOfferFiltersFromParams } from 'lib/cruises/cruiseUtils'

import { useGlobalCruisesSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalCruisesSearch'
import useCruiseSearchCount from 'hooks/Cruise/useCruiseSearchCount'
import { useAppDispatch } from 'hooks/reduxHooks'
import useQueryParams from 'hooks/useQueryParams'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import useOfferList from 'hooks/Offers/useOfferList'

import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'

import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import Heading from 'LuxKit/Typography/Heading'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Pane from 'components/Common/Pane'
import OfferListCarouselTrack from 'components/OfferList/OfferListCarouselTrack'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import CruiseSearchFiltersBar from 'components/Cruises/SearchPage/Filters/CruiseSearchFiltersBar'
import CruiseSortDropdown from 'components/Cruises/SearchPage/Filters/Inputs/CruiseSortDropdown'
import CruiseHeaderFilterChip from 'components/Cruises/SearchPage/Filters/CruiseHeaderFilterChip'
import CruiseHeadData from 'components/Cruises/Common/CruiseHeadData'
import CruisesContact from 'components/Cruises/Common/CruisesContact'

import { getListName } from 'analytics/enhanced-ecommerce'
import getOfferListKey from 'lib/offer/offerListKey'
import type { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'
import { setLogicalScrollLeft } from 'LuxKit/Carousel/utils'

const GridWrapper = styled.div`
  row-gap: ${rem(40)};
  column-gap: ${rem(24)};

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: 1fr 1fr 1fr;
  }

  > * {
    width: 100%;
  }
`

const CruiseSearchPanel = styled.div`
  position: relative;
  padding: ${rem(20)} 0;
`

const StyledLayoutContainer = styled(LayoutContainer)`
  padding-inline: 0;
  padding-top: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding-inline: ${rem(16)};
  }

  ${mediaQueryUp.desktop} {
    && {
      max-width: ${rem(1352)};
    }
  }
`

const CountWrapper = styled(Group)`
  padding-inline-start: ${rem(16)};

  ${mediaQueryUp.tablet} {
    padding-inline-start: 0;
  }
`

type InheritedOfferListCarouselTrackProps = Pick<
  ComponentProps<typeof OfferListCarouselTrack>,
  'additionalOfferUrlQuery' | 'tabletLimit' | 'limit' | 'tileStyle'
>

interface Props extends PropsWithChildren, InheritedOfferListCarouselTrackProps, App.ComponentCruiseOfferList {
  /**
   * Exclude the following offer ids from the results
   */
  excludedIds?: Array<string>
  /**
   * Override the duration and the price with the checkIn/checkOut/Occupancy
   */
  selectedDatesAndRoomsFilter?: App.OfferListFilters
}

function ContentfulCruiseOfferGrid(props: Props) {
  const intl = useIntl()
  const queryParams = useQueryParams()
  const dispatch = useAppDispatch()
  const { search } = useLocation()

  const initialState = useMemo(() => ({
    ...getCruiseSearchStateFromParams(queryParams),
    ...{
      departureIds: props.departurePlaceIds,
      destinationIds: props.destinationPlaceIds,
      cruiseLines: props.cruiseLines,
      durationMin: props.durationMin,
      durationMax: props.durationMax,
      departureMonths: props.departureMonths,
    },
  }), [queryParams, props])

  const { globalCruisesSearchDispatch, globalCruisesSearchState } = useGlobalCruisesSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    ...initialState,
  })

  const [filters, filtersSelected] = useMemo(() => {
    const [baseFilters, hasFilters] = getCruiseOfferFiltersFromParams(queryParams)
    const sortBy = (queryParams.get('sortBy') ?? 'recommended') as App.OfferListSortByOptions
    return [{
      ...baseFilters,
      sortBy,
      departureIds: props.departurePlaceIds,
      destinationIds: props.destinationPlaceIds,
      cruiseLines: props.cruiseLines,
      durationMin: props.durationMin,
      durationMax: props.durationMax,
      departureMonths: props.departureMonths,
    },
    hasFilters]
  }, [queryParams, props])

  const tracking = useOfferListTracking(
    getListName('OfferList', 'CRUISE', 'Contentful Grid View'),
    { key: getOfferListKey(filters) },
  )
  const [count, fetchingSearchCount] = useCruiseSearchCount(filters)

  const filterOfferList = useOfferList(
    filters,
    {
      disabled: !filters,
      excludedIds: props.excludedIds,
      minRequiredOffers: filters?.limit,
    },
  )

  const offerList = filterOfferList
  const carouselRef = useRef<HTMLDivElement | null>(null)

  const [inViewRef, carouselInView] = useInView({
    triggerOnce: true,
    rootMargin: '500px',
  })

  useEffect(() => {
    // We've been given a new offer list, send the carousel back to the start
    if (carouselRef.current) {
      setLogicalScrollLeft(carouselRef.current, 0)
    }
  }, [offerList])

  useEffect(() => {
    inViewRef(carouselRef.current)
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselRef.current])

  const onSortChange = useCallback((nextSort: App.OfferListSortByOptions) => {
    const nextSearch = setSearchParamValue(search, 'sortBy', nextSort)
    dispatch(replace({ search: nextSearch }))
  }, [dispatch, search])

  const handleSortChange = useCallback<SortControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
    }))
    window.scrollTo({ behavior: 'instant', top: 0 })
  }, [dispatch])

  const searchingForCruisesText = intl.formatMessage({
    id: 'Wz-GR4k9So39wLXUAVMp',
    defaultMessage: 'Searching for cruises',
    description: 'Loading text shown while searching for cruise offers',
  })

  const track = <OfferListCarouselTrack
    inView={carouselInView}
    offerList={offerList}
    tileStyle="card-default"
    additionalOfferUrlQuery={props.additionalOfferUrlQuery}
    limit={props.limit}
    tabletLimit={props.tabletLimit}
    selectedDatesAndRoomsFilter={props.selectedDatesAndRoomsFilter}
  >
    {props.children}
  </OfferListCarouselTrack>

  return (
    <AnalyticsPageContext.Provider value={AnalyticsPage.search}>
      <CruiseHeadData />
      <GlobalSearchContextProvider
        state={globalCruisesSearchState}
        dispatch={globalCruisesSearchDispatch}
      >
        <CSSBreakpoint as={Pane} min="tablet">
          <LayoutContainer>
            <CruiseSearchPanel>
              <CruiseSearchFiltersBar />
            </CruiseSearchPanel>
            <CruiseHeaderFilterChip filters={filters} onSortChange={handleSortChange} />
          </LayoutContainer>
        </CSSBreakpoint>
        <StyledLayoutContainer>
          <OfferListEventsProvider tracking={tracking}>
            <LayoutContainer>
              <Group direction="vertical" gap={8}>
                <Heading variant="heading3" dangerouslySetInnerHTML={{ __html: props.title }} />
                {(fetchingSearchCount || !!count || filtersSelected) &&
                  <CountWrapper
                    direction="horizontal"
                    gap={8}
                    horizontalAlign="space-between"
                    desktopVerticalAlign="center"
                    tabletVerticalAlign="end"
                  >
                    <Group direction="horizontal" gap={16} verticalAlign="center">
                      <BodyText variant="large">
                        {fetchingSearchCount && searchingForCruisesText}
                        {!fetchingSearchCount && <FormattedMessage
                          id="Cekruq07oynOgo-Ok1RP"
                          defaultMessage="{count, plural, one {# cruise found} other {# cruises found}}"
                          description="Count of cruises found in cruise offer grid search results"
                          values={{ count }}
                        />}
                      </BodyText>
                      {fetchingSearchCount && <LoadingAnimation size={8} />}
                    </Group>

                    <CSSBreakpoint min="desktop">
                      <CruiseSortDropdown sort={filters.sortBy ?? 'recommended'} onChange={onSortChange} />
                    </CSSBreakpoint>
                  </CountWrapper>}
                <GridWrapper ref={carouselRef}>
                  {track}
                </GridWrapper>
              </Group>
            </LayoutContainer>
          </OfferListEventsProvider>
        </StyledLayoutContainer>
        <CruisesContact />
      </GlobalSearchContextProvider>
    </AnalyticsPageContext.Provider>
  )
}

export default ContentfulCruiseOfferGrid
